import { LANGUAGE_OPTIONS, type Language } from '@/admin/languages';
import type { components } from '@/api/qraft/schema';

// Plain module: route validateSearch runs in the public entry chunk, and
// importing it from the component file would pull the UI kit in with it.
export type ContentStatus = components['schemas']['ContentStatus'];
export type QuestionType = components['schemas']['QuestionTypes'];

// Kept in the URL, so Back from a result returns to the same search.
export type ContentSearchParams = {
  q?: string;
  status?: ContentStatus;
  language?: Language;
  type?: QuestionType;
  page?: number;
};

export const STATUSES: ContentStatus[] = ['draft', 'published'];
const LANGUAGES = LANGUAGE_OPTIONS.map(([code]) => code);

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]) =>
  allowed.includes(value as T) ? (value as T) : undefined;

export function parseContentSearch(
  s: Record<string, unknown>,
  types: readonly QuestionType[] = [],
): ContentSearchParams {
  const page = Number(s.page);
  // The router reads a bare `?q=29060` as a number, so an ID search in a link
  // arrives as one.
  const q = typeof s.q === 'number' ? String(s.q) : s.q;
  return {
    q: typeof q === 'string' && q !== '' ? q : undefined,
    status: oneOf(s.status, STATUSES),
    language: oneOf(s.language, LANGUAGES),
    type: oneOf(s.type, types),
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

// The query the search endpoints take, from what's in the URL.
export function searchQuery(params: ContentSearchParams, pageSize: number) {
  return {
    ...(params.q ? { search: params.q } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.language ? { language: params.language } : {}),
    limit: pageSize,
    offset: ((params.page ?? 1) - 1) * pageSize,
  };
}
